import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { format, parseISO } from 'date-fns';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { MemoHeatmap } from './MemoHeatmap';
import { cn } from '@/lib/utils';

interface DateNavigatorProps {
    selectedDate: string; // format: 'yyyy-MM-dd'
    onDateChange: (date: string) => void;
    onTodayClick?: () => void; // Custom handler for Today button (e.g., to clear filters)
    disabled?: boolean; // Disable date navigation when type filter is active
    filterSlot?: React.ReactNode; // Slot for filter icon
    className?: string;
    children?: React.ReactNode;
}

export function DateNavigator({ selectedDate, onDateChange, onTodayClick, disabled = false, filterSlot, className, children }: DateNavigatorProps) {
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);

    // Fetch all dates that have memos
    const { data: datesData } = useQuery({
        queryKey: ['memo-dates'],
        queryFn: api.getMemoDates,
    });

    // Sort dates for navigation
    const sortedDates = useMemo(() => {
        if (!datesData?.data) return [];
        return [...datesData.data].sort();
    }, [datesData]);

    // Check if today is selected
    const today = format(new Date(), 'yyyy-MM-dd');
    const isToday = selectedDate === today;

    // Find current index and prev/next dates
    const currentIndex = sortedDates.indexOf(selectedDate);

    // Find the closest previous and next dates with memos
    // If current date has no memos (currentIndex === -1), find dates before/after it
    const prevDate = useMemo(() => {
        if (sortedDates.length === 0) return null;

        if (currentIndex === -1) {
            // Current date has no memos, find the closest previous date with memos
            const previousDates = sortedDates.filter(d => d < selectedDate);
            return previousDates.length > 0 ? previousDates[previousDates.length - 1] : null;
        }

        // Current date has memos, get the previous date in the sorted list
        return currentIndex > 0 ? sortedDates[currentIndex - 1] : null;
    }, [sortedDates, currentIndex, selectedDate]);

    const nextDate = useMemo(() => {
        if (sortedDates.length === 0) return null;

        if (currentIndex === -1) {
            // Current date has no memos, find the closest next date with memos
            const nextDates = sortedDates.filter(d => d > selectedDate);
            return nextDates.length > 0 ? nextDates[0] : null;
        }

        // Current date has memos, get the next date in the sorted list
        return currentIndex < sortedDates.length - 1 ? sortedDates[currentIndex + 1] : null;
    }, [sortedDates, currentIndex, selectedDate]);

    const hasPrev = prevDate !== null;
    const hasNext = nextDate !== null;

    const handlePrevDate = () => {
        if (prevDate) {
            onDateChange(prevDate);
        }
    };

    const handleNextDate = () => {
        if (nextDate) {
            onDateChange(nextDate);
        }
    };

    const handleTodayClick = () => {
        if (onTodayClick) {
            onTodayClick();
        } else {
            onDateChange(today);
        }
    };

    const handleDateSelect = (date: string | undefined) => {
        if (date) {
            onDateChange(date);
            setIsPopoverOpen(false);
        }
    };

    // Format display date
    const displayDate = useMemo(() => {
        if (disabled) {
            return 'All Dates';
        }
        try {
            const date = parseISO(selectedDate);
            return format(date, 'MMM dd, yyyy');
        } catch {
            return selectedDate;
        }
    }, [selectedDate, disabled]);

    return (
        <div
            className={cn(
                "fixed bottom-6 left-1/2 -translate-x-1/2 z-50",
                "w-full max-w-2xl px-4",
                "flex items-center justify-center gap-3",
                className
            )}
        >
            {/* Date Navigator */}
            <div
                className={cn(
                    "flex items-center justify-between gap-1.5 px-3 py-2.5",
                    "max-w-[280px] sm:max-w-sm",
                    "bg-white/95 dark:bg-zinc-900/95 backdrop-blur-md",
                    "rounded-full shadow-lg shadow-zinc-900/10 dark:shadow-zinc-950/50",
                    "border border-zinc-200/50 dark:border-zinc-800/50",
                    "transition-all duration-300 ease-out",
                    "hover:shadow-xl hover:shadow-zinc-900/15 dark:hover:shadow-zinc-950/60",
                )}
            >
                {/* Previous Date Button */}
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={handlePrevDate}
                    disabled={disabled || !hasPrev}
                    className="h-7 w-7 sm:h-8 sm:w-8 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-30 transition-all"
                    aria-label="Previous date with memos"
                >
                    <ChevronLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                </Button>

                {/* Date Display with Popover */}
                <Popover open={!disabled && isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                    <PopoverTrigger asChild>
                        <Button
                            variant="ghost"
                            disabled={disabled}
                            className={cn(
                                "h-auto py-1 px-2 sm:py-1.5 sm:px-3 transition-colors rounded-full min-w-[110px] sm:min-w-[130px] max-w-[110px] sm:max-w-[130px]",
                                disabled
                                    ? "cursor-not-allowed opacity-60"
                                    : "hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
                            )}
                        >
                            <span className="text-xs sm:text-sm font-medium text-zinc-900 dark:text-zinc-100 whitespace-nowrap truncate">
                                {displayDate}
                            </span>
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent
                        className="w-auto p-0"
                        align="center"
                        side="top"
                        sideOffset={12}
                    >
                        <MemoHeatmap
                            selectedDate={selectedDate}
                            onDateSelect={handleDateSelect}
                            initialMonth={selectedDate}
                            className="border-0 shadow-none"
                        />
                    </PopoverContent>
                </Popover>

                {/* Today Button (always reserve space to prevent layout shift) */}
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleTodayClick}
                    disabled={!disabled && isToday}
                    className={cn(
                        "h-7 w-7 sm:h-8 sm:w-8 rounded-full transition-all",
                        !disabled && isToday
                            ? "invisible pointer-events-none"
                            : "hover:bg-zinc-100 dark:hover:bg-zinc-800"
                    )}
                    aria-label="Go to today"
                    title={disabled ? "Clear filter" : "Today"}
                >
                    <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                </Button>

                {/* Next Date Button */}
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleNextDate}
                    disabled={disabled || !hasNext}
                    className="h-7 w-7 sm:h-8 sm:w-8 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-30 transition-all"
                    aria-label="Next date with memos"
                >
                    <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                </Button>
            </div>

            {/* Filter slot (e.g., MemoTypeFilter) */}
            {filterSlot}

            {/* Right side content (e.g., FAB button) */}
            {children}
        </div>
    );
}
